import { useState } from "react"
import { useTranslation } from "react-i18next"

interface Merchant {
  id: string
  name: string
  domain: string
}

interface MerchantSelectorProps {
  merchants: Merchant[]
  onMerchantSelect: (merchantId: string) => void
  selectedMerchantForDisplay: string
}

export const MerchantSelector = ({
  merchants,
  onMerchantSelect,
  selectedMerchantForDisplay,
}: MerchantSelectorProps) => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState("")

  const handleMerchantClick = (merchantId: string) => {
    const merchant = merchants.find((m) => m.id === merchantId)
    if (merchant) onMerchantSelect(`${merchant.name} (${merchant.domain})`)

    setSearchTerm("")
  }

  // Check if input is a full UUID (merchant ID)
  const isFullMerchantId = (input: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(input.trim())
  }

  // Handle direct merchant ID input
  const handleDirectMerchantId = (merchantId: string) => {
    onMerchantSelect(`Merchant ID: ${merchantId}`)
    setSearchTerm("")
  }

  return (
    <div className="bg-dark-secondary rounded-lg border border-gray-700 p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">{t("analytics.selectMerchant")}</h3>
      </div>

      <div className="w-full relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("analytics.searchMerchants")}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        {searchTerm && merchants && (
          <div className="absolute z-10 w-full mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
            {/* Show direct merchant ID option if input is a full UUID */}
            {isFullMerchantId(searchTerm) && (
              <div
                className="px-4 py-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 transition-colors bg-blue-900/20"
                onClick={() => handleDirectMerchantId(searchTerm.trim())}
              >
                <div className="font-medium text-blue-300 text-sm">
                  {t("analytics.searchByMerchantId")}
                </div>
                <div className="text-gray-400 text-xs truncate mt-1">{searchTerm.trim()}</div>
                <div className="text-gray-500 text-xs mt-1">
                  {t("analytics.directSearchDescription")}
                </div>
              </div>
            )}

            {/* Show regular merchant search results */}
            {merchants
              .filter(
                (merchant) =>
                  merchant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  merchant.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  merchant.id.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .slice(0, 10)
              .map((merchant) => (
                <div
                  key={merchant.id}
                  className="px-4 py-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 transition-colors"
                  onClick={() => handleMerchantClick(merchant.id)}
                >
                  <div className="font-medium text-white text-sm truncate">{merchant.name}</div>
                  <div className="text-gray-400 text-xs truncate mt-1">{merchant.domain}</div>
                  <div className="text-gray-500 text-xs truncate mt-1 font-mono">
                    {t("analytics.merchantId")}: {merchant.id}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {selectedMerchantForDisplay && (
        <div className="mt-3 p-3 bg-gray-800 rounded-md border border-gray-600">
          <div className="text-sm text-gray-300">
            <span className="font-medium text-white">{t("analytics.selectedMerchant")}:</span>
            <div className="mt-1 text-white font-medium">{selectedMerchantForDisplay}</div>
          </div>
        </div>
      )}
    </div>
  )
}
